import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { ChevronRight, Printer, QrCode } from 'lucide-react'
import { api } from '@/lib/api'
import { formatDate } from '@/lib/date'
import { useAuth } from '@/context/AuthContext'
import type { Job, Paginated } from '@/types'
import Card from '@/components/ui/Card'
import PageHeader from '@/components/ui/PageHeader'
import Button from '@/components/ui/Button'
import Pager from '@/components/ui/Pager'
import { Input } from '@/components/ui/Field'
import { Select } from '@/components/ui/Select'
import { StateBadge } from '@/components/ui/Badge'
import { Table, Row, Cell, EmptyRow, actionsColumn, actionsCellClass } from '@/components/ui/Table'
import { cn } from '@/lib/cn'
import BalanceBadge from '@/components/job/BalanceBadge'
import UrgentBadge from '@/components/job/UrgentBadge'

type Tab = 'queue' | 'history' | 'mine'

// `short` is the phone label, where the three pills share one row equally.
const TABS: { key: Tab; label: string; short: string }[] = [
  { key: 'queue', label: 'Queue', short: 'Queue' },
  { key: 'history', label: 'Section history', short: 'History' },
  { key: 'mine', label: 'My work', short: 'My work' },
]

const EMPTY: Record<Tab, string> = {
  queue: 'No jobs in your section right now.',
  history: 'Your section has not worked any jobs yet.',
  mine: 'You have not logged work on any job yet.',
}

/**
 * The full staff job list. Queue is the live section queue; history keeps
 * every job the section has logged work on, completed included; my work is
 * the jobs this operator personally scanned.
 */
export default function StaffJobs() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [tab, setTab] = useState<Tab>('queue')
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [page, setPage] = useState(1)

  const switchTab = (next: Tab) => {
    setTab(next)
    setStatus('')
    setPage(1)
  }

  const params: Record<string, string | number> = { scope: tab, page }
  if (search) params.search = search
  if (status) params.status = status

  const { data } = useQuery({
    queryKey: ['staff-jobs', tab, { search, status, page }],
    queryFn: async () => (await api.get<Paginated<Job>>('/staff/jobs', { params })).data,
  })

  const jobs = data?.data ?? []

  // The queue only ever holds active statuses, so completed is not offered there.
  const statusOptions = [
    { value: 'queued', label: 'Queued' },
    { value: 'in_progress', label: 'In progress' },
    { value: 'issue', label: 'Issue' },
    ...(tab === 'queue' ? [] : [{ value: 'completed', label: 'Completed' }]),
  ]

  const sectionName = (job: Job) =>
    job.current_department?.name ?? (job.status === 'completed' ? 'Done' : '-')

  return (
    <div>
      <PageHeader
        title="My jobs"
        subtitle={`${user?.department?.name ?? 'Production'} section - live queue, history and your own work`}
      />

      <Card className="mb-5">
        <div className="flex flex-wrap items-end gap-3 sm:gap-4">
          {/* Three equal pills on phones, so nothing scrolls or clips; natural widths from sm up. */}
          <div className="grid w-full grid-cols-3 gap-1.5 sm:flex sm:w-auto">
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => switchTab(t.key)}
                className={cn(
                  'min-w-0 whitespace-nowrap rounded-lg border px-2 py-2 text-center text-sm font-semibold sm:shrink-0 sm:px-3.5',
                  tab === t.key
                    ? 'border-primary/60 bg-primary/20 text-primary'
                    : 'border-line text-muted hover:bg-foreground/5',
                )}
              >
                <span className="sm:hidden">{t.short}</span>
                <span className="hidden sm:inline">{t.label}</span>
              </button>
            ))}
          </div>
          {/* One shared row on phones: status keeps a 9rem basis that fits
              "In progress", search takes the rest. A basis, not w-36: Select
              already carries inputClass's w-full, and cn() cannot override it.
              Fixed widths from sm up. */}
          <div className="flex w-full items-end gap-2 sm:w-auto sm:gap-3">
            <Input
              placeholder="Job code..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              className="min-w-0 flex-1 sm:w-48 sm:flex-none"
            />
            <Select
              value={status}
              onChange={(value) => {
                setStatus(value)
                setPage(1)
              }}
              options={statusOptions}
              placeholder="Any status"
              className="min-w-0 flex-[0_0_9rem] sm:w-40 sm:flex-none"
            />
          </div>
        </div>
      </Card>

      {/* Phones get tappable cards, same as the scan queue. */}
      <div className="space-y-3 sm:hidden">
        {jobs.length === 0 && (
          <div className="box px-4 py-10 text-center text-sm text-foreground/60">{EMPTY[tab]}</div>
        )}
        {jobs.map((job) => (
          // A clickable div rather than a <button>: the Sheet shortcut nests
          // inside, and interactive elements cannot nest. Same recipe as
          // clickable table rows - stopPropagation on the inner control.
          <div
            key={job.id}
            onClick={() => navigate(`/staff/jobs/${job.qr_token}`)}
            className="box w-full cursor-pointer p-4 text-left transition-colors active:bg-foreground/5"
          >
            {/* Badges grouped on the right and the code kept whole: spread out
                as three items, an urgent job's code broke in two at 375px. */}
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
              <span className="whitespace-nowrap font-mono text-xs font-bold">{job.code}</span>
              <span className="flex items-center gap-1.5">
                <BalanceBadge balance={job.balance} />
                <UrgentBadge urgent={job.order?.is_urgent} />
                <StateBadge state={job.status} />
              </span>
            </div>
            <div className="mt-2.5 flex items-center justify-between gap-3 text-sm">
              <span className="min-w-0">
                <span className="block truncate text-foreground">{job.order?.agent?.name}</span>
                <span className="mt-0.5 block text-xs text-foreground/60">
                  {job.order?.number} - {sectionName(job)}
                </span>
                {job.current_department_due && (
                  <span className={cn('mt-0.5 block text-xs', job.late ? 'font-semibold text-status-danger' : 'text-foreground/60')}>
                    Due {formatDate(job.current_department_due)}
                  </span>
                )}
              </span>
              <span className="flex shrink-0 items-center gap-2">
                <button
                  type="button"
                  aria-label="Print job sheet"
                  onClick={(e) => {
                    e.stopPropagation()
                    window.open(`/staff/jobs/${job.id}/sheet`, '_blank', 'noopener')
                  }}
                  className="-m-2 p-2 text-foreground/40 transition-colors active:text-foreground"
                >
                  <Printer className="size-4" />
                </button>
                <ChevronRight className="size-4 text-foreground/40" />
              </span>
            </div>
          </div>
        ))}
      </div>

      <div className="hidden sm:block">
        <Table columns={['Job code', 'Order', 'Customer', 'Due', 'Status', 'Section', actionsColumn]}>
          {jobs.length === 0 && <EmptyRow colSpan={7}>{EMPTY[tab]}</EmptyRow>}
          {jobs.map((job) => (
            <Row key={job.id}>
              <Cell className="font-mono text-xs font-bold">
                {job.code}
                <UrgentBadge urgent={job.order?.is_urgent} className="ml-1.5" />
                <BalanceBadge balance={job.balance} className="ml-1.5" />
              </Cell>
              <Cell>{job.order?.number}</Cell>
              <Cell>{job.order?.agent?.name}</Cell>
              {/* Completed jobs carry no section deadline - the cell stays quiet. */}
              <Cell className={cn(job.late && 'font-semibold text-status-danger')}>
                {job.current_department_due ? formatDate(job.current_department_due) : '-'}
              </Cell>
              <Cell>
                <StateBadge state={job.status} />
              </Cell>
              <Cell>{sectionName(job)}</Cell>
              <Cell className={actionsCellClass}>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => navigate(`/staff/jobs/${job.qr_token}`)}>
                    <QrCode className="size-4" /> Open
                  </Button>
                  <Button variant="outline" onClick={() => window.open(`/staff/jobs/${job.id}/sheet`, '_blank', 'noopener')}>
                    <Printer className="size-4" /> Sheet
                  </Button>
                </div>
              </Cell>
            </Row>
          ))}
        </Table>
      </div>

      <Pager meta={data?.meta} page={page} onPage={setPage} />
    </div>
  )
}
